import { ragaTargetCents } from '../seed/target.js';
/**
 * "Anchor + approach" drill templates, expressed as scale-STEP offsets from
 * the target swara (0 = the target itself; -1/+1 = its immediate in-raga
 * neighbours; -2/+2 = a skip-neighbour "leap"). Level 1 is the gentlest and
 * shortest; higher levels add length and wider leaps around the target.
 *
 * Every template both starts and ends near/on the target and keeps the
 * target at >=40% of its notes, so `generateDrill` doesn't have to re-derive
 * prominence — it just has to pick a template and lay it on the grid.
 */
const DRILL_TEMPLATES = {
    // lower-neighbour, target, upper-neighbour, target, target (held)
    1: [-1, 0, 1, 0, 0],
    // one more approach cycle before settling
    2: [-1, 0, 1, 0, -1, 0, 0],
    // wider (skip-neighbour) leaps before settling
    3: [-2, -1, 0, 1, 2, 1, 0, -1, 0, 0],
};
/**
 * Map a scale-STEP index (relative to Sa, i.e. already `targetIdx + offset`)
 * to a (swara symbol, octave) against a raga's ladder. Generalises
 * `generate.ts`'s `degreeToSwara` to ladders of any length (janya scales can
 * have 5, 6 or 7 in-scale swaras), and gracefully wraps out-of-range indices
 * into the neighbouring octave (e.g. one step below Sa -> the raga's highest
 * swara, mandra sthayi; one step above the top swara -> Sa, tara sthayi).
 */
function stepToSwara(index, syms) {
    const n = syms.length;
    if (index >= 0 && index < n)
        return { swara: syms[index], octave: 0 };
    if (index >= n)
        return { swara: syms[index - n], octave: 1 };
    return { swara: syms[index + n], octave: -1 };
}
/**
 * A short, gentle drill that anchors + approaches a target swara in a raga:
 * a small "neighbour, target, neighbour, target, target(hold)" figure laid
 * on the thaalam beat grid. Pure function: no DOM, no `Date.now`, no
 * side effects — same inputs always produce the same `NotationTimeline`.
 *
 * The target swara is kept prominent (>=40% of the notes) and is always the
 * longest-held note (its final landing gets double duration), so a learner's
 * ear settles on it. Neighbours are the target's actual scale-neighbours in
 * THIS raga (from `Object.keys(raga.swara_variants)`, ascending, S first) —
 * absent swaras (e.g. in a pentatonic raga) are simply not adjacent, so they
 * never appear. At the scale's edges (target = Sa or the top swara) the
 * missing neighbour wraps an octave (mandra Ni below Sa, tara Sa above the
 * top swara) instead of crashing or emitting an undefined swara.
 */
export function generateDrill(raga, targetSwara, thaalamName = 'Adi', tempoBpm = 60, opts) {
    const syms = Object.keys(raga.swara_variants);
    const targetIdx = syms.indexOf(targetSwara);
    if (targetIdx === -1) {
        throw new Error(`generateDrill: target swara "${targetSwara}" is not in ${raga.name}'s scale`);
    }
    const level = opts?.level ?? 1;
    const steps = DRILL_TEMPLATES[level];
    const lastTargetStepIdx = steps.lastIndexOf(0); // the note the drill lands/holds on
    const cents = ragaTargetCents(raga);
    let cursor = 0;
    const notes = steps.map((offset, index) => {
        const { swara, octave } = stepToSwara(targetIdx + offset, syms);
        const durationBeats = index === lastTargetStepIdx ? 2 : 1; // hold the target's final landing
        const note = {
            index,
            swara,
            octave,
            startBeat: cursor,
            durationBeats,
            targetCents: cents[swara] + 1200 * octave,
        };
        cursor += durationBeats;
        return note;
    });
    return {
        exerciseId: `drill-${raga.id}-${targetSwara}-l${level}`,
        raga: raga.name,
        thaalam: thaalamName,
        tempoBpm,
        notes,
        totalBeats: cursor,
    };
}
//# sourceMappingURL=drill.js.map